import type { FormField } from "@/hooks/schema/use-schema";
import type { Entity, EntityField, EntityRelated } from "@/services/entity.service";

/**
 * Builds the `FormField`-shaped schemas the admin panel uses for entity CRUD
 * (forms, tables, query/mutation generation) directly from the cached entity
 * registry (`entityList`), instead of a full GraphQL IntrospectionQuery.
 *
 * This is possible because the entity registry is a faithful mirror of the
 * generated GraphQL schema:
 *  - scalar fields  = `entity_fields` where `is_readable` (the backend's own
 *    SDL-inclusion predicate; FK/internal columns are excluded there).
 *  - relation fields = `entity_relations` (this entity is the source →
 *    `field_name`) + `entity_related` (this entity is the target →
 *    `target_field_name`), typed `<Target>Data` / `<Target>ListData` by
 *    cardinality — mirroring the backend's `has_multiple` rule.
 *  - `*_variants` localization fields = one `FieldVariantsData` object field per
 *    field that has a localization variant (`variant_entity_field`).
 *
 * The shapes returned here are structurally identical to what the old
 * introspection-derived `useSchemaStore.getEntitySchema` produced, so every
 * downstream consumer keeps working unchanged.
 */

export interface EntityFormSchema {
  fields: FormField[];
  name: string;
  id?: string;
}

export interface EntityFormSchemas {
  entitySchema: EntityFormSchema | undefined;
  entitySchemaWithRelations: EntityFormSchema | undefined;
  entitySchemaWithRelationsCreateInputTypes: EntityFormSchema | undefined;
  entitySchemaWithRelationsUpdateInputTypes: EntityFormSchema | undefined;
}

// `FieldVariantsData.data` (a `FieldVariant`) — the scalar leaves the query
// builder fetches for localized `*_variants` fields.
const FIELD_VARIANTS_NESTED_FIELDS: FormField[] = [
  {
    name: "is_default",
    kind: "SCALAR",
    type: "Boolean",
    required: false,
    list: false,
    fields: null,
  },
  {
    name: "variant",
    kind: "SCALAR",
    type: "PrimitiveType",
    required: false,
    list: false,
    fields: null,
  },
  {
    name: "value",
    kind: "SCALAR",
    type: "PrimitiveType",
    required: false,
    list: false,
    fields: null,
  },
];

// Cardinality → `ListData` suffix, mirroring the backend's `has_multiple`.
const isToManyForward = (type: string): boolean => type === "OneToMany" || type === "ManyToMany";
const isToManyBackward = (type: string): boolean => type === "ManyToOne" || type === "ManyToMany";

// Enum fields surface as a GraphQL ENUM type (kind "ENUM"); everything else is a
// plain scalar. Matches the introspected kind so query/column logic is unchanged.
const scalarKind = (dataType: string): FormField["kind"] =>
  String(dataType).toLowerCase() === "enum" ? "ENUM" : "SCALAR";

const buildScalarField = (field: EntityField, localeFieldId?: string): FormField => ({
  name: field.name,
  kind: scalarKind(field.data_type as string),
  // The introspected schema's scalar type is replaced with the registry
  // `data_type` (e.g. "TEXT", "ENUM") — consumers key off this, not the GraphQL
  // scalar name.
  type: field.data_type as string,
  required: false,
  list: false,
  fields: null,
  isLocalized: localeFieldId ? field.variant_entity_field?.data?.id === localeFieldId : false,
  formType: field.form_type,
  formatDecimals: field.format_decimals,
  formatGrouping: field.format_grouping,
  formatCurrency: field.format_currency,
  formatLocale: field.format_locale,
  validationString: field.validation_string,
});

const buildScalarFields = (entity: Entity, localeFieldId?: string): FormField[] =>
  (entity.entity_fields?.data ?? [])
    // `is_readable` is the registry's "exposed as a GraphQL scalar" flag.
    // Treat a missing flag as readable so nothing is dropped unexpectedly.
    .filter((f) => f.is_readable !== false)
    .map((f) => buildScalarField(f, localeFieldId));

const buildVariantFields = (entity: Entity): FormField[] =>
  (entity.entity_fields?.data ?? [])
    .filter((f) => !!f.variant_entity_field?.data?.id)
    .map((f) => ({
      name: `${f.name}_variants`,
      kind: "OBJECT" as const,
      type: "FieldVariantsData",
      required: false,
      list: false,
      fields: FIELD_VARIANTS_NESTED_FIELDS,
    }));

const makeRelationField = (
  fieldName: string,
  targetName: string,
  toMany: boolean,
  getEntity: (name: string) => Entity | undefined,
  localeFieldId: string | undefined,
  depth: number,
): FormField => {
  // Resolve the target entity's own fields one level deep (the query builder and
  // column logic read these). Deeper levels are left unresolved (fields: null),
  // matching the introspection store's single-level resolution.
  let nested: FormField[] | null = null;
  if (depth > 0) {
    const target = getEntity(targetName);
    if (target) nested = buildAllFields(target, getEntity, localeFieldId, depth - 1);
  }
  return {
    name: fieldName,
    kind: "OBJECT",
    type: `${targetName}${toMany ? "ListData" : "Data"}`,
    required: false,
    list: false,
    fields: nested,
  };
};

const buildRelationFields = (
  entity: Entity,
  getEntity: (name: string) => Entity | undefined,
  localeFieldId: string | undefined,
  depth: number,
): FormField[] => {
  const fields: FormField[] = [];
  for (const r of entity.entity_relations?.data ?? ([] as EntityRelated[])) {
    const target = r.target_entity?.data?.name;
    if (!r.field_name || !target) continue;
    fields.push(
      makeRelationField(
        r.field_name,
        target,
        isToManyForward(r.type),
        getEntity,
        localeFieldId,
        depth,
      ),
    );
  }
  for (const r of entity.entity_related?.data ?? ([] as EntityRelated[])) {
    const target = r.entity?.data?.name;
    if (!r.target_field_name || !target) continue;
    fields.push(
      makeRelationField(
        r.target_field_name,
        target,
        isToManyBackward(r.type),
        getEntity,
        localeFieldId,
        depth,
      ),
    );
  }
  return fields;
};

const buildAllFields = (
  entity: Entity,
  getEntity: (name: string) => Entity | undefined,
  localeFieldId: string | undefined,
  depth: number,
): FormField[] => [
  ...buildScalarFields(entity, localeFieldId),
  ...buildVariantFields(entity),
  ...buildRelationFields(entity, getEntity, localeFieldId, depth),
];

export const buildEntityFormSchemas = (
  entity: Entity | null | undefined,
  getEntity: (name: string) => Entity | undefined,
  localeFieldId?: string,
): EntityFormSchemas => {
  if (!entity) {
    return {
      entitySchema: undefined,
      entitySchemaWithRelations: undefined,
      entitySchemaWithRelationsCreateInputTypes: undefined,
      entitySchemaWithRelationsUpdateInputTypes: undefined,
    };
  }

  const schema: EntityFormSchema = {
    name: entity.name,
    id: entity.id,
    fields: buildAllFields(entity, getEntity, localeFieldId, 1),
  };

  // The four shapes were distinct in the introspection store (output type vs
  // create/update input types), but every live consumer reads the same fields
  // (and only `.id` from the create-input shape), so one shape serves all.
  return {
    entitySchema: schema,
    entitySchemaWithRelations: schema,
    entitySchemaWithRelationsCreateInputTypes: schema,
    entitySchemaWithRelationsUpdateInputTypes: schema,
  };
};
